import { isTestFile } from '@/lib/file-paths/is-test-file';

export function createPrV2FileFilter(hideTestFiles: boolean, revealedFilenames: readonly string[]) {
  const revealed = new Set(revealedFilenames);
  return (file: { filename: string }) =>
    !hideTestFiles || revealed.has(file.filename) || !isTestFile(file.filename);
}

export function filterPrV2FileGroups<T extends { files: { filename: string }[] }>(
  groups: T[],
  isVisible: (file: { filename: string }) => boolean,
): T[] {
  return groups.flatMap((group) => {
    const files = group.files.filter(isVisible);
    return files.length ? [{ ...group, files }] : [];
  });
}

export type PrV2FileStats = { fileCount: number; additions: number; deletions: number };

type PrV2FileWithStats = { filename: string; additions: number; deletions: number };

/**
 * Sums file count and line counts over the files the predicate selects. The
 * shared primitive behind the viewer's visible-file stats and the mirrored
 * test-file share (see `seedPrV2PullRequestFileMirror`).
 */
export function sumPrV2FileStats(
  files: readonly PrV2FileWithStats[],
  include: (file: { filename: string }) => boolean,
): PrV2FileStats {
  return files.reduce(
    (stats, file) => {
      if (include(file)) {
        stats.fileCount += 1;
        stats.additions += file.additions;
        stats.deletions += file.deletions;
      }
      return stats;
    },
    { fileCount: 0, additions: 0, deletions: 0 },
  );
}

/**
 * The test-file share of the PR's diff that the file mirror stored, which is only meaningful for
 * the head it was hydrated from: null once a push moves the head on, until a files sync stores
 * the new head's. See the writer comment in `seedPrV2PullRequestFileMirror`
 * (src/lib/pr-v2/mirror-write-through.ts).
 */
export function getPrV2MirroredTestFileStats(pullRequest: {
  headSha: string;
  filesHydratedHeadSha: string | null;
  testFileCount: number | null;
  testFileAdditions: number | null;
  testFileDeletions: number | null;
}): PrV2FileStats | null {
  if (
    pullRequest.filesHydratedHeadSha !== pullRequest.headSha ||
    pullRequest.testFileCount === null ||
    pullRequest.testFileAdditions === null ||
    pullRequest.testFileDeletions === null
  ) {
    return null;
  }

  return {
    fileCount: pullRequest.testFileCount,
    additions: pullRequest.testFileAdditions,
    deletions: pullRequest.testFileDeletions,
  };
}

export type PrV2VisibleFileStats = PrV2FileStats & { hiddenTestFileCount: number };

export function getPrV2VisibleFileStats(
  files: readonly PrV2FileWithStats[],
  isVisible: (file: { filename: string }) => boolean,
): PrV2VisibleFileStats {
  const visible = sumPrV2FileStats(files, isVisible);

  return { ...visible, hiddenTestFileCount: files.length - visible.fileCount };
}

/** A diff's code / tests split and the total it splits, as the line-count tooltips list them. */
export type PrV2DiffStatsBreakdown = {
  code: PrV2FileStats;
  tests: PrV2FileStats;
  total: PrV2FileStats;
};

export type PrV2DiffStats = PrV2DiffStatsBreakdown & {
  /** What the diff shows: the test-file filter plus any per-file reveals. */
  visible: PrV2VisibleFileStats;
};

/** The visible stats plus the code / tests split behind the PR header's breakdown. */
export function getPrV2DiffStats(
  files: readonly PrV2FileWithStats[],
  isVisible: (file: { filename: string }) => boolean,
): PrV2DiffStats {
  const isTest = (file: { filename: string }) => isTestFile(file.filename);

  return {
    visible: getPrV2VisibleFileStats(files, isVisible),
    code: sumPrV2FileStats(files, (file) => !isTest(file)),
    tests: sumPrV2FileStats(files, isTest),
    total: sumPrV2FileStats(files, () => true),
  };
}
